import { RouteGuardPermissions } from "@/components/permissions/route-guard-multiple";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  WorkOrderProvider,
  useWorkOrderContext,
} from "@/components/operations/work-orders/use-work-order-context";
import {
  WorkOrderDetailHeader,
  WorkOrderTitleBlock,
  WorkOrderNotes,
  WorkOrderProperties,
} from "@/components/operations/work-orders/work-order-header";
import WorkOrderTabsContent, {
  type WorkOrderTabId,
} from "@/components/operations/work-orders/work-order-tabs-content";
import WorkOrderLogsSection, {
  WorkOrderLogsSectionSkeleton,
} from "@/components/operations/work-orders/work-order-logs-section";
import { Id } from "@/convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import { ClipboardList, ArrowLeft, PanelRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";
import { useState, useCallback, useMemo, useEffect } from "react";
import PageNavigationTabs from "@/components/navigation/page-navigation-tabs";
import type { NavigationTab } from "@/components/navigation/page-navigation-tabs";

type TabSearchParams = {
  tab?: WorkOrderTabId;
};

export const Route = createFileRoute(
  "/_authenticated/operations/work-orders/$id",
)({
  validateSearch: (search: Record<string, unknown>): TabSearchParams => {
    const validTabs: WorkOrderTabId[] = [
      "personnel",
      "equipment",
      "expenses",
      "services",
      "products",
      "attachments",
      "documents",
    ];
    const tab = search.tab as WorkOrderTabId | undefined;
    return {
      tab: tab && validTabs.includes(tab) ? tab : undefined,
    };
  },
  component: WorkOrderLayout,
});

function WorkOrderLayout() {
  const { id } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem("wo-properties-sidebar-collapsed");
    return saved !== null ? saved !== "true" : true;
  });
  const [sidebarHydrated, setSidebarHydrated] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("wo-properties-sidebar-collapsed");
    if (saved !== null) {
      setSidebarOpen(saved !== "true");
    }
    const timer = setTimeout(() => setSidebarHydrated(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (sidebarHydrated) {
      localStorage.setItem(
        "wo-properties-sidebar-collapsed",
        String(!sidebarOpen),
      );
    }
  }, [sidebarOpen, sidebarHydrated]);

  const activeTab: WorkOrderTabId = tab ?? "personnel";

  const handleTabChange = useCallback(
    (newTab: WorkOrderTabId) => {
      navigate({
        to: "/operations/work-orders/$id",
        params: { id },
        search: newTab === "personnel" ? {} : { tab: newTab },
        replace: true,
      });
    },
    [navigate, id],
  );

  return (
    <RouteGuardPermissions
      permissions={[{ module: "work_orders", actions: ["read"] }]}
    >
      <WorkOrderProvider workOrderId={id}>
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* ── Top header bar ── */}
          <div className="flex items-center justify-between border-b border-border/60 shrink-0">
            <div className="flex-1 min-w-0">
              <WorkOrderDetailHeader />
            </div>
            {/* Sidebar toggle (desktop) */}
            <button
              type="button"
              onClick={() => setSidebarOpen((v) => !v)}
              className={cn(
                "hidden lg:flex items-center justify-center h-10 w-10 shrink-0 border-l border-border/60 transition-colors hover:bg-muted/50",
                !sidebarOpen && "text-muted-foreground",
              )}
            >
              <PanelRight className="h-4 w-4" />
            </button>
          </div>

          {/* ── Body: main + sidebar ── */}
          <div className="flex-1 flex min-h-0 overflow-hidden">
            {/* Main column */}
            <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
              <WorkOrderNotFoundGuard>
                {/* Title + description */}
                <WorkOrderTitleBlock />

                {/* Tab navigation */}
                <div className="px-5 pt-2 shrink-0">
                  <WorkOrderNavigation
                    workOrderId={id}
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                  />
                </div>

                {/* Tab content */}
                <div className="px-5 pt-2">
                  <WorkOrderTabsContent activeTab={activeTab} />
                </div>

                {/* Notas (editor markdown) — debajo de los tabs */}
                <div className="px-5 pt-3">
                  <WorkOrderNotes />
                </div>

                {/* Activity — always visible below tabs */}
                <ActivitySection workOrderId={id} />
              </WorkOrderNotFoundGuard>
            </div>

            {/* Properties sidebar (desktop) — animated like AI chat sidebar */}
            <div
              className={cn(
                "fixed inset-0 bg-black/50 z-40 lg:hidden",
                !sidebarOpen && "hidden",
              )}
              onClick={() => setSidebarOpen(false)}
            />
            <aside
              className={cn(
                "hidden lg:flex flex-col border-l border-border/60 shrink-0 overflow-hidden bg-muted/20 transition-all duration-300",
                sidebarOpen ? "w-80" : "w-0 border-l-0",
              )}
            >
              <div className="w-80 shrink-0 overflow-y-auto">
                <WorkOrderProperties />
              </div>
            </aside>
          </div>

          {/* Mobile properties collapsible */}
          <MobilePropertiesBar />
        </div>
      </WorkOrderProvider>
    </RouteGuardPermissions>
  );
}

/** Shows not-found state if workOrder is null after loading */
function WorkOrderNotFoundGuard({ children }: { children: React.ReactNode }) {
  const ctx = useWorkOrderContext();
  const navigate = useNavigate();

  if (!ctx.isLoading && !ctx.workOrder) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 py-20 gap-4">
        <div className="p-4 bg-muted rounded-full">
          <ClipboardList className="h-8 w-8 text-muted-foreground" />
        </div>
        <div className="text-center">
          <p className="text-base font-semibold">Orden no encontrada</p>
          <p className="text-sm text-muted-foreground mt-1">
            Esta orden no existe o fue eliminada.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate({ to: "/operations/work-orders" })}
        >
          <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
          Volver al listado
        </Button>
      </div>
    );
  }

  return <>{children}</>;
}

/** Collapsible drawer for mobile — shows properties at bottom */
function MobilePropertiesBar() {
  const [open, setOpen] = useState(false);

  return (
    <div className="lg:hidden shrink-0 border-t border-border/60">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-medium text-muted-foreground hover:bg-muted/50 transition-colors"
      >
        <span className="flex items-center gap-1.5">
          <PanelRight className="h-3.5 w-3.5" />
          Propiedades
        </span>
        <span className={cn("transition-transform", open && "rotate-180")}>
          ▾
        </span>
      </button>
      {open && (
        <div className="border-t border-border/40 max-h-72 overflow-y-auto bg-muted/10">
          <WorkOrderProperties />
        </div>
      )}
    </div>
  );
}

// ─── Tab navigation ───────────────────────────────────────────────────────────

interface WorkOrderNavigationProps {
  workOrderId: string;
  activeTab: WorkOrderTabId;
  onTabChange: (tab: WorkOrderTabId) => void;
}

function WorkOrderNavigation({
  workOrderId,
  activeTab,
  onTabChange,
}: WorkOrderNavigationProps) {
  const ctx = useWorkOrderContext();
  const { can } = usePermissions();

  const tabs = useMemo<NavigationTab[]>(() => {
    if (ctx.isLoading || !ctx.workOrder) return [];

    return [
      ...(can("read", "work_orders")
        ? [
            {
              name: "Personal",
              href: "#personnel",
              icon: "Users",
              badge: ctx.workOrder.assignedPersonnel.length || undefined,
            } satisfies NavigationTab,
          ]
        : []),
      ...(can("read", "work_orders")
        ? [
            {
              name: "Equipos",
              href: "#equipment",
              icon: "Truck",
              badge: ctx.workOrder.assignedEquipment.length || undefined,
            } satisfies NavigationTab,
          ]
        : []),
      ...(can("read", "work_orders")
        ? [
            {
              name: "Gastos",
              href: "#expenses",
              icon: "Receipt",
              badge: ctx.workOrder.expenses.length || undefined,
            } satisfies NavigationTab,
          ]
        : []),
      ...(can("read", "work_orders")
        ? [
            {
              name: "Servicios",
              href: "#services",
              icon: "Briefcase",
              badge: ctx.workOrder.assignedServices?.length || undefined,
            } satisfies NavigationTab,
          ]
        : []),
      ...(can("read", "work_orders")
        ? [
            {
              name: "Productos",
              href: "#products",
              icon: "Package",
              badge: ctx.workOrder.assignedProducts?.length || undefined,
            } satisfies NavigationTab,
          ]
        : []),
      ...(can("read", "attachments")
        ? [
            {
              name: "Archivos",
              href: "#attachments",
              icon: "Paperclip",
            } satisfies NavigationTab,
          ]
        : []),
      ...(can("read", "document_records")
        ? [
            {
              name: "Documentos",
              href: "#documents",
              icon: "FileText",
            } satisfies NavigationTab,
          ]
        : []),
    ];
  }, [ctx.isLoading, ctx.workOrder, can]);

  const handleTabClick = useCallback(
    (e: React.MouseEvent, href: string) => {
      e.preventDefault();
      const tabId = href.replace("#", "") as WorkOrderTabId;
      onTabChange(tabId);
    },
    [onTabChange],
  );

  if (ctx.isLoading || !ctx.workOrder) return null;

  return (
    <PageNavigationTabs
      tabs={tabs}
      variant="view-toggle"
      size="sm"
      showIcons={true}
      scrollable={true}
      alignment="left"
      activeHref={`#${activeTab}`}
      onTabClick={handleTabClick}
    />
  );
}

// ─── Activity section (always visible below tabs) ─────────────────────────────

function ActivitySection({ workOrderId }: { workOrderId: string }) {
  const ctx = useWorkOrderContext();
  const { can } = usePermissions();

  if (!can("read", "work_orders")) return null;

  return (
    <div className="px-5 pt-3 pb-5 border-t border-border/40 mt-1">
      {ctx.isLoading || ctx.logsLoading ? (
        <WorkOrderLogsSectionSkeleton />
      ) : (
        <WorkOrderLogsSection
          workOrderId={workOrderId as Id<"work_orders">}
          logs={ctx.logs}
        />
      )}
    </div>
  );
}
